/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "./Button";
import { cn } from "../../lib/utils";
import { useDialogFocus } from "./useDialogFocus";
import { overlayHost } from "./overlayHost";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBeforeClose?: () => boolean | Promise<boolean>;
  children: React.ReactNode;
  title?: string;
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  asSheet?: boolean;
}

const sizeClasses = {
  sm: "md:min-w-80 md:max-w-md",
  md: "md:min-w-96 md:max-w-xl",
  lg: "md:min-w-[32rem] md:max-w-2xl",
  xl: "md:min-w-[40rem] md:max-w-3xl",
  full: "md:min-w-[48rem] md:max-w-4xl",
};

/**
 * Every modal renders into the shared {@link overlayHost}. Closing on a click outside means a click
 * on the backdrop itself: children that portal out of the panel (a Select popper, a date picker)
 * are neither inside the panel nor on the backdrop.
 */

export function Modal({
  isOpen,
  onClose,
  onBeforeClose,
  children,
  title,
  headerActions,
  footer,
  size = "md",
  asSheet = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const titleId = useId();

  const handleClose = useCallback(async () => {
    if (onBeforeClose) {
      const canClose = await onBeforeClose();
      if (!canClose) return;
    }
    onClose();
  }, [onClose, onBeforeClose]);

  useDialogFocus(modalRef, isOpen);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      document.body.style.overflow = "unset";
      setIsAnimating(false);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-50 flex",
        asSheet
          ? "items-end md:items-center md:justify-center"
          : "items-center justify-center"
      )}
    >
      <div
        data-modal-backdrop
        onMouseDown={handleClose}
        className={cn(
          "fixed inset-0 bg-black/55 backdrop-blur-md transition-opacity duration-200",
          isAnimating ? "opacity-100" : "opacity-0"
        )}
      />
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        style={
          asSheet
            ? {
                bottom: isAnimating ? 0 : "-100%",
                willChange: "bottom, opacity",
              }
            : undefined
        }
        className={cn(
          "bg-bg-elevated shadow-elevated overflow-hidden duration-200 ease-out border border-border focus:outline-none",
          asSheet
            ? cn("fixed left-0 right-0 md:relative w-full md:w-fit rounded-t-3xl md:rounded-3xl max-h-[90vh] md:max-h-[85vh] transition-[bottom,opacity]", sizeClasses[size])
            : "relative w-[calc(100%-2rem)] max-w-xl rounded-3xl max-h-[85vh] transition-opacity",
          isAnimating ? "opacity-100" : "opacity-0"
        )}
      >
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 id={titleId} className="font-display text-lg text-text-primary">
              {title}
            </h2>
            <div className="flex items-center gap-2">
              {headerActions}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClose}
                aria-label="Close"
              >
                <X size={20} />
              </Button>
            </div>
          </div>
        )}
        <div
          data-modal-content
          className={cn(
            "overflow-y-auto",
            footer
              ? "max-h-[calc(90vh-160px)] md:max-h-[calc(85vh-160px)]"
              : "max-h-[calc(90vh-80px)] md:max-h-[calc(85vh-80px)]"
          )}
        >
          {children}
        </div>
        {footer && (
          <div className="border-t border-border bg-bg-elevated">{footer}</div>
        )}
      </div>
    </div>,
    overlayHost()
  );
}
